import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Palette, Settings, FileText, ClipboardList, Plus, Trash2,
  Save, RefreshCw, Mail, Upload, GripVertical, Eye, Sparkles,
  Crown, X, Users, Check, ArrowRight, ArrowUp, ArrowDown, Type, List, CheckSquare,
  AlignLeft, Star, Scissors, Heart, ChefHat, Droplets, Shield,
  Home, Globe, Clock, MessageSquare, Image as ImageIcon, Layers,
  ChevronRight, ChevronLeft, Monitor, Smartphone, ExternalLink, Wand2
} from "lucide-react";

// Icon options
const ICONS = {
  sparkles: Sparkles, scissors: Scissors, heart: Heart, crown: Crown,
  palette: Palette, chef: ChefHat, star: Star, droplets: Droplets, users: Users,
  clipboard: ClipboardList
};

// Default fallback presets (used if API fails)
const DEFAULT_CATEGORY_PRESETS = [
  { 
    id: "makeup", 
    name: "Makeup Artists", 
    icon: "sparkles", 
    color: "#ec4899",
    description: "For MUAs, beauty artists, bridal makeup",
    category_type: "service_provider"
  },
  { 
    id: "hair", 
    name: "Hair Stylists", 
    icon: "scissors", 
    color: "#8b5cf6",
    description: "For braiders, colorists, barbers, stylists",
    category_type: "service_provider"
  },
  { 
    id: "nail", 
    name: "Nail Technicians", 
    icon: "palette", 
    color: "#f43f5e",
    description: "For nail artists, manicurists",
    category_type: "service_provider"
  },
  { 
    id: "lash", 
    name: "Lash Artists", 
    icon: "star", 
    color: "#a855f7",
    description: "For lash extensions, brow artists",
    category_type: "service_provider"
  },
  { 
    id: "wellness", 
    name: "Wellness Professionals", 
    icon: "heart", 
    color: "#10b981",
    description: "For massage therapists, estheticians, spa services",
    category_type: "service_provider"
  },
  { 
    id: "culinary", 
    name: "Culinary Professionals", 
    icon: "chef", 
    color: "#f97316",
    description: "For private chefs, caterers, bakers",
    category_type: "service_provider"
  },
  { 
    id: "style", 
    name: "Retail & Fashion", 
    icon: "crown", 
    color: "#6366f1",
    description: "For clothing, shoes, accessories, jewelry retailers",
    category_type: "service_provider"
  },
  { 
    id: "instructor", 
    name: "Instructors & Educators", 
    icon: "clipboard", 
    color: "#0ea5e9",
    description: "For workshops, classes, education providers",
    category_type: "instructor_pilot"
  },
  { 
    id: "events", 
    name: "Events & Experiences", 
    icon: "users", 
    color: "#3b82f6",
    description: "For events, mixers, galas (HTHF style)",
    category_type: "event_pilot"
  },
  { 
    id: "custom", 
    name: "Start from Scratch", 
    icon: "crown", 
    color: "#6b7280",
    description: "Create your own custom template"
  },
];

// Investor portal presets
const INVESTOR_PRESETS = [
  {
    id: "angel_investor",
    name: "Angel Investor",
    icon: "star",
    color: "#10b981",
    description: "For angel investors and early-stage backers",
    category_type: "investor",
    portal_type: "investors"
  },
  {
    id: "vc_fund",
    name: "VC / Fund",
    icon: "crown",
    color: "#3b82f6",
    description: "For venture capital firms and fund managers",
    category_type: "investor",
    portal_type: "investors"
  },
  {
    id: "strategic_partner",
    name: "Strategic Partner",
    icon: "users",
    color: "#8b5cf6",
    description: "For strategic partners and advisory investors",
    category_type: "investor",
    portal_type: "investors"
  },
  {
    id: "custom_investor",
    name: "Start from Scratch",
    icon: "crown",
    color: "#6b7280",
    description: "Create a custom investor portal"
  },
];

// Support portal presets
const SUPPORT_PRESETS = [
  {
    id: "team",
    name: "Team Portal",
    icon: "users",
    color: "#3b82f6",
    description: "Internal team collaboration portal",
    category_type: "support",
    portal_type: "support"
  },
  {
    id: "investor",
    name: "Investor Portal",
    icon: "briefcase",
    color: "#10b981",
    description: "For investor relations and updates",
    category_type: "support",
    portal_type: "support"
  },
  {
    id: "network",
    name: "Network Portal",
    icon: "globe",
    color: "#8b5cf6",
    description: "For community networking",
    category_type: "support",
    portal_type: "support"
  },
  {
    id: "client",
    name: "Client Portal",
    icon: "user",
    color: "#f97316",
    description: "For client communication and access",
    category_type: "support",
    portal_type: "support"
  },
  {
    id: "public",
    name: "Public Portal",
    icon: "eye",
    color: "#ec4899",
    description: "Public-facing information portal",
    category_type: "support",
    portal_type: "support"
  },
  {
    id: "custom",
    name: "Start from Scratch",
    icon: "crown",
    color: "#6b7280",
    description: "Create your own custom portal"
  }
];

// Color presets
const COLORS = [
  { name: "Pink", value: "#ec4899" },
  { name: "Purple", value: "#8b5cf6" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Violet", value: "#a855f7" },
  { name: "Orange", value: "#f97316" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Amber", value: "#f59e0b" },
];

// Question types
const QUESTION_TYPES = [
  { value: "text", label: "Short Text", icon: Type },
  { value: "textarea", label: "Long Text", icon: AlignLeft },
  { value: "select", label: "Single Select", icon: List },
  { value: "multiselect", label: "Multi-select", icon: CheckSquare },
  { value: "yesno", label: "Yes/No", icon: Check },
  { value: "toggle", label: "Toggle", icon: List },
  { value: "file", label: "File Upload", icon: Upload },
];

// Phase/Step configuration
const PHASES = [
  { id: "envelope", label: "Envelope", icon: Mail, description: "The initial invite animation" },
  { id: "welcome", label: "Welcome", icon: Home, description: "Welcome page after opening" },
  { id: "nda", label: "NDA", icon: Shield, description: "Non-disclosure agreement" },
  { id: "intake", label: "Intake", icon: ClipboardList, description: "Onboarding questionnaire" },
  { id: "portal", label: "Portal", icon: Layers, description: "Member dashboard" },
];

// Real-time intake question preview component
function IntakeQuestionPreview({ question, color }) {
  const renderPreview = () => {
    switch (question.type) {
      case "text":
      case "email":
      case "phone":
        return <Input placeholder={question.placeholder || "Type here..."} disabled className="bg-gray-50" />;
      case "textarea":
        return <Textarea placeholder={question.placeholder || "Type here..."} disabled rows={2} className="bg-gray-50" />;
      case "multiselect":
        return (
          <div className="flex flex-wrap gap-2">
            {(question.options || ["Option 1", "Option 2", "Option 3"]).slice(0, 4).map((opt, i) => (
              <button
                key={i}
                className="px-3 py-1.5 rounded-full border-2 text-sm transition-colors"
                style={{ borderColor: i === 0 ? color : "#e5e7eb", color: i === 0 ? color : "#6b7280" }}
              >
                {typeof opt === "object" ? opt.label : opt}
              </button>
            ))}
            {question.has_other && (
              <button className="px-3 py-1.5 rounded-full border-2 border-dashed border-gray-300 text-sm text-gray-400">+ Other</button>
            )}
          </div>
        );
      case "select":
        return (
          <select className="w-full p-2 rounded-lg border bg-gray-50 text-sm text-gray-600" disabled>
            <option value="">Select an option...</option>
            {(question.options || ["Option 1", "Option 2"]).map((opt, i) => (
              <option key={i} value={typeof opt === "object" ? opt.value : opt}>
                {typeof opt === "object" ? opt.label : opt}
              </option>
            ))}
          </select>
        );
      case "yesno":
        return (
          <div className="flex gap-3">
            <button className="flex-1 py-2 rounded-lg border-2 text-sm font-medium" style={{ borderColor: color, color }}>Yes</button>
            <button className="flex-1 py-2 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-500">No</button>
          </div>
        );
      case "toggle":
        return (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{question.description || "Toggle option"}</span>
            <Switch checked disabled />
          </div>
        );
      case "file":
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
            <p className="text-xs text-gray-500">Click to upload</p>
          </div>
        );
      default:
        return <Input placeholder="..." disabled className="bg-gray-50" />;
    }
  };

  return (
    <div className="p-3 border rounded-lg bg-white">
      <Label className="text-sm font-medium flex items-center gap-1 mb-2">
        {question.label || "Question"}
        {question.required && <span className="text-red-500">*</span>}
      </Label>
      {renderPreview()}
    </div>
  );
}

export default function PortalConfiguratorModal({ 
  isOpen, 
  onClose, 
  parentCategory = "pilots",
  existingPortal = null,
  onSuccess
}) {
  const [step, setStep] = useState(existingPortal ? "configure" : "select"); // "select" or "configure"
  const [activeTab, setActiveTab] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // Portal templates from API - use appropriate defaults based on parentCategory
  const defaultPresets = parentCategory === "support" ? SUPPORT_PRESETS : parentCategory === "investors" ? INVESTOR_PRESETS : DEFAULT_CATEGORY_PRESETS;
  const [categoryPresets, setCategoryPresets] = useState(defaultPresets);
  const [loadingPresets, setLoadingPresets] = useState(false);
  
  // Reset presets when parentCategory changes
  useEffect(() => {
    const newDefaults = parentCategory === "support" ? SUPPORT_PRESETS : parentCategory === "investors" ? INVESTOR_PRESETS : DEFAULT_CATEGORY_PRESETS;
    setCategoryPresets(newDefaults);
  }, [parentCategory]);
  
  // Fetch templates from API when modal opens
  useEffect(() => {
    if (isOpen && !existingPortal) {
      fetchPortalTemplates();
    }
  }, [isOpen, existingPortal]);
  
  const fetchPortalTemplates = async () => {
    setLoadingPresets(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-templates`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Portal templates API response:", data); // Debug log
        
        // Filter templates based on parentCategory context
        const templates = (data.templates || [])
          .filter(t => {
            // Accept both "live" status and templates without status (backwards compat)
            const isLive = !t.status || t.status === "live" || t.status === "active";
            if (!isLive) return false;
            
            // Filter by parentCategory
            if (parentCategory === "support") {
              // Support portals: show only support templates
              return t.category_type === "support" || t.portal_type === "support";
            } else if (parentCategory === "integrations") {
              // Integrations: show integration templates
              return t.category_type === "integration" || t.portal_type === "integration";
            } else {
              // Pilots: show pilot/service provider templates (exclude support and integration)
              const isSupport = t.category_type === "support" || t.portal_type === "support";
              const isIntegration = t.category_type === "integration" || t.portal_type === "integration";
              return !isSupport && !isIntegration;
            }
          })
          .map(t => ({
            id: t.template_id,
            name: t.name,
            icon: t.icon,
            color: t.color,
            description: t.description,
            category_type: t.category_type,
            portal_type: t.portal_type,
            features: t.features || [],
            requires_nda: t.requires_nda,
            bypass_intake: t.bypass_intake
          }));
        
        console.log("Filtered templates for", parentCategory, ":", templates.length); // Debug log
        
        // Add "custom" option at the end
        templates.push({
          id: "custom",
          name: "Start from Scratch",
          icon: "crown",
          color: "#6b7280",
          description: "Create your own custom portal"
        });
        
        // Always update presets if we got any templates (including just custom)
        if (templates.length >= 1) {
          setCategoryPresets(templates);
        }
      } else {
        console.error("Portal templates API failed:", res.status);
      }
    } catch (err) {
      console.error("Failed to fetch portal templates:", err);
      // Keep default presets on error
    } finally {
      setLoadingPresets(false);
    }
  };
  
  // Basic info
  const [basics, setBasics] = useState({
    name: "",
    description: "",
    slug: "",
    icon: "sparkles",
    is_active: true,
  });
  
  // Combined Branding + Phase Templates
  const [branding, setBranding] = useState({
    primary_color: "#ec4899",
    secondary_color: "#1a1a2e",
    logo_url: "",
    envelope_text: "YOU'RE INVITED",
    envelope_bg_color: "#1a1a2e",
    envelope_text_color: "#ffffff",
    envelope_animation: "fade",
    welcome_title: "",
    welcome_subtitle: "",
    welcome_show_demo: true,
    nda_title: "Pilot Partner Agreement",
    nda_show_stealth: true,
    nda_checkbox_text: "I agree to keep all pilot information confidential",
    portal_welcome: "Welcome to your portal!",
    portal_show_checklist: true,
    portal_show_updates: true,
  });
  
  // Phase settings
  const [phases, setPhases] = useState({
    envelope: { enabled: true },
    welcome: { enabled: true },
    nda: { enabled: true },
    intake: { enabled: true },
    portal: { enabled: true },
  });
  
  // Intake sections
  const [sections, setSections] = useState([]);
  const [previewSection, setPreviewSection] = useState(0);
  
  // Reset when modal opens
  // Track the last loaded portal ID to avoid infinite loops
  const lastLoadedPortalIdRef = useRef(null);
  
  useEffect(() => {
    if (!isOpen) {
      // Reset the ref when modal closes
      lastLoadedPortalIdRef.current = null;
      return;
    }
    
    const portalId = existingPortal?.sub_category_id || null;
    
    // Only load if this is a different portal or first load
    if (portalId !== lastLoadedPortalIdRef.current) {
      lastLoadedPortalIdRef.current = portalId;
      
      if (existingPortal) {
        setStep("configure");
        loadExistingPortal();
      } else {
        setStep("select");
        setSelectedPreset(null);
        resetForm();
      }
    }
  }, [isOpen, existingPortal?.sub_category_id]);
  
  const loadExistingPortal = async () => {
    setBasics({
      name: existingPortal.name || "",
      description: existingPortal.description || "",
      slug: existingPortal.slug || "",
      icon: existingPortal.icon || "sparkles",
      is_active: existingPortal.is_active !== false,
    });
    if (existingPortal.branding) {
      setBranding(prev => ({ ...prev, ...existingPortal.branding }));
    }
    if (existingPortal.phase_templates) {
      setPhases(prev => ({ ...prev, ...existingPortal.phase_templates }));
    }
    
    // Load sections - first check if the portal has its own sections
    if (existingPortal.sections?.length > 0) {
      setSections(existingPortal.sections);
    } else {
      // Try to load from the template using the preset/category
      const category = existingPortal.preset_id || existingPortal.category || 
                       existingPortal.name?.toLowerCase().split(/[- ]/)[0];
      if (category) {
        await loadIntakeTemplate(category);
      }
    }
  };
  
  const resetForm = () => {
    setBasics({ name: "", description: "", slug: "", icon: "sparkles", is_active: true });
    setBranding({
      primary_color: "#ec4899",
      secondary_color: "#1a1a2e",
      logo_url: "",
      envelope_text: "YOU'RE INVITED",
      envelope_bg_color: "#1a1a2e",
      envelope_text_color: "#ffffff",
      envelope_animation: "fade",
      welcome_title: "",
      welcome_subtitle: "",
      welcome_show_demo: true,
      nda_title: "Pilot Partner Agreement",
      nda_show_stealth: true,
      nda_checkbox_text: "I agree to keep all pilot information confidential",
      portal_welcome: "Welcome to your portal!",
      portal_show_checklist: true,
      portal_show_updates: true,
    });
    setPhases({
      envelope: { enabled: true },
      welcome: { enabled: true },
      nda: { enabled: true },
      intake: { enabled: true },
      portal: { enabled: true },
    });
    setSections([]);
  };
  
  // Load preset when selected
  const handleSelectPreset = async (preset) => {
    setSelectedPreset(preset);
    setLoadingTemplate(true);
    
    // Pre-fill basics from preset (title, description, icon, color)
    setBasics(prev => ({
      ...prev,
      name: preset.name, // Pre-fill title from preset name
      slug: preset.id !== "custom" ? preset.id : "",
      description: preset.description, // Pre-fill description from preset
      icon: preset.icon,
    }));
    
    setBranding(prev => ({
      ...prev,
      primary_color: preset.color,
    }));
    
    if (preset.id !== "custom") {
      // Load template from backend
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/intake-template/${preset.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.sections?.length > 0) {
            setSections(data.sections);
          }
          if (data.branding) {
            setBranding(prev => ({ ...prev, ...data.branding, primary_color: preset.color }));
          }
        }
      } catch (err) {
        console.error("Error loading preset:", err);
      }
    }
    
    setLoadingTemplate(false);
    setStep("configure");
    setActiveTab("basics");
  };
  
  const loadIntakeTemplate = async (category) => {
    setLoadingTemplate(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/intake-template/${category}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sections?.length > 0) {
          setSections(data.sections);
        }
        if (data.branding) {
          setBranding(prev => ({ ...prev, ...data.branding }));
        }
      }
    } catch (err) {
      console.error("Error loading template:", err);
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").trim();
  
  const updateBasics = (field, value) => {
    setBasics(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "name" && !existingPortal) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };
  
  const updateBranding = (field, value) => setBranding(prev => ({ ...prev, [field]: value }));
  
  const togglePhase = (phaseId) => {
    setPhases(prev => ({
      ...prev,
      [phaseId]: { ...prev[phaseId], enabled: !prev[phaseId]?.enabled }
    }));
  };
  
  // Section management with drag-and-drop
  const addSection = () => {
    setSections(prev => [...prev, {
      id: `section_${Date.now()}`,
      title: "New Section",
      subtitle: "",
      icon: "Settings",
      questions: []
    }]);
  };
  
  const updateSection = (idx, field, value) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  
  const deleteSection = (idx) => setSections(prev => prev.filter((_, i) => i !== idx));
  
  const moveSectionUp = (idx) => {
    if (idx === 0) return;
    setSections(prev => {
      const newArr = [...prev];
      [newArr[idx - 1], newArr[idx]] = [newArr[idx], newArr[idx - 1]];
      return newArr;
    });
    setPreviewSection(prev => prev === idx ? idx - 1 : prev === idx - 1 ? idx : prev);
  };
  
  const moveSectionDown = (idx) => {
    if (idx >= sections.length - 1) return;
    setSections(prev => {
      const newArr = [...prev];
      [newArr[idx], newArr[idx + 1]] = [newArr[idx + 1], newArr[idx]];
      return newArr;
    });
    setPreviewSection(prev => prev === idx ? idx + 1 : prev === idx + 1 ? idx : prev);
  };
  
  // Question management with drag-and-drop
  const addQuestion = (sectionIdx) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return {
        ...s,
        questions: [...s.questions, {
          id: `q_${Date.now()}`,
          type: "text",
          label: "New Question",
          required: false,
          placeholder: "",
          options: [],
          has_other: false
        }]
      };
    }));
  };
  
  const updateQuestion = (sectionIdx, qIdx, field, value) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return {
        ...s,
        questions: s.questions.map((q, j) => j === qIdx ? { ...q, [field]: value } : q)
      };
    }));
  };
  
  const deleteQuestion = (sectionIdx, qIdx) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return { ...s, questions: s.questions.filter((_, j) => j !== qIdx) };
    }));
  };
  
  const moveQuestionUp = (sectionIdx, qIdx) => {
    if (qIdx === 0) return;
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      const newQuestions = [...s.questions];
      [newQuestions[qIdx - 1], newQuestions[qIdx]] = [newQuestions[qIdx], newQuestions[qIdx - 1]];
      return { ...s, questions: newQuestions };
    }));
  };
  
  const moveQuestionDown = (sectionIdx, qIdx) => {
    const section = sections[sectionIdx];
    if (!section || qIdx >= section.questions.length - 1) return;
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      const newQuestions = [...s.questions];
      [newQuestions[qIdx], newQuestions[qIdx + 1]] = [newQuestions[qIdx + 1], newQuestions[qIdx]];
      return { ...s, questions: newQuestions };
    }));
  };
  
  // Save
  const handleSave = async () => {
    if (!basics.name.trim()) {
      toast.error("Portal name is required");
      setActiveTab("basics");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const slug = basics.slug || generateSlug(basics.name);
      
      const payload = {
        name: basics.name,
        description: basics.description,
        slug,
        icon: basics.icon,
        is_active: basics.is_active,
        branding,
        phase_templates: phases,
        sections,
        preset_id: selectedPreset?.id || null,
      };
      
      let res;
      if (existingPortal) {
        res = await fetch(`${API}/admin/portal-categories/${parentCategory}/sub-categories/${existingPortal.sub_category_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API}/admin/portal-categories/${parentCategory}/sub-categories`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      }
      
      if (!res.ok) throw new Error("Failed to save");
      
      // Also save intake template using preset_id (category) not slug
      const templateCategory = selectedPreset?.id || basics.slug;
      await fetch(`${API}/admin/pilot-templates/${templateCategory}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sections, branding, category: templateCategory })
      });
      
      toast.success(existingPortal ? "Portal updated!" : "Portal created!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Failed to save portal");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  const currentSection = sections[previewSection];
  
  // ============================================================================
  // RENDER: Preset Selection Step
  // ============================================================================
  if (step === "select") {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              Select a Template
            </DialogTitle>
            <DialogDescription>
              Choose a category preset to get started with pre-configured colors and intake questions
            </DialogDescription>
          </DialogHeader>
          
          {loadingPresets ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-500">Loading templates...</span>
            </div>
          ) : (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="grid grid-cols-2 gap-4 py-4">
                {categoryPresets.map((preset) => {
                  const Icon = ICONS[preset.icon] || Sparkles;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handleSelectPreset(preset)}
                      className="p-6 rounded-xl border-2 border-gray-200 hover:border-current transition-all text-left group hover:shadow-lg"
                      style={{ 
                        "--tw-border-opacity": 1,
                        borderColor: "rgb(229 231 235)",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = preset.color}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = "rgb(229 231 235)"}
                      data-testid={`preset-${preset.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                          style={{ backgroundColor: `${preset.color}20` }}
                        >
                          <Icon className="w-7 h-7" style={{ color: preset.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{preset.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{preset.description}</p>
                          {preset.id !== "custom" && (
                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="outline" className="text-xs" style={{ borderColor: preset.color, color: preset.color }}>
                                Pre-configured
                              </Badge>
                              <span className="text-xs text-gray-400">Colors + Questions</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
          
          {loadingTemplate && (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-500">Loading template...</span>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // ============================================================================
  // RENDER: Configuration Step
  // ============================================================================
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                {selectedPreset && !existingPortal && (
                  <Button variant="ghost" size="sm" onClick={() => setStep("select")} className="mr-2 -ml-2">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                )}
                <Settings className="w-5 h-5" />
                {existingPortal ? `Edit ${basics.name || "Portal"}` : "Configure Portal"}
              </DialogTitle>
              <DialogDescription>
                {selectedPreset && !existingPortal ? (
                  <span className="flex items-center gap-2 mt-1">
                    Template: <Badge style={{ backgroundColor: selectedPreset.color }}>{selectedPreset.name}</Badge>
                    <span className="text-gray-400">- Customize as needed</span>
                  </span>
                ) : (
                  "Configure branding, invite flow, and intake questions"
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start px-6 py-2 bg-gray-50 border-b rounded-none h-auto gap-1">
            <TabsTrigger value="basics" className="data-[state=active]:bg-white">
              <Settings className="w-4 h-4 mr-2" /> Basics
            </TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-white">
              <Palette className="w-4 h-4 mr-2" /> Branding & Flow
            </TabsTrigger>
            <TabsTrigger value="intake" className="data-[state=active]:bg-white">
              <ClipboardList className="w-4 h-4 mr-2" /> Intake Questions
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-white">
              <Eye className="w-4 h-4 mr-2" /> Preview
            </TabsTrigger>
          </TabsList>
          
          <ScrollArea className="flex-1 min-h-0">
            {/* BASICS TAB */}
            <TabsContent value="basics" className="p-6 space-y-6 m-0">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Portal Name *</Label>
                    <Input
                      value={basics.name}
                      onChange={(e) => updateBasics("name", e.target.value)}
                      placeholder="e.g., Makeup Artists, Hair Stylists"
                      data-testid="portal-name-input"
                    />
                  </div>
                  <div>
                    <Label>URL Slug</Label>
                    <Input
                      value={basics.slug}
                      onChange={(e) => updateBasics("slug", e.target.value)}
                      placeholder="makeup-artists"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={basics.description}
                      onChange={(e) => updateBasics("description", e.target.value)}
                      placeholder="Brief description..."
                      rows={3}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Icon</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(ICONS).map(([key, Icon]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => updateBasics("icon", key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            basics.icon === key ? "scale-105 shadow-md" : "border-gray-200 hover:border-gray-300"
                          }`}
                          style={{ borderColor: basics.icon === key ? branding.primary_color : undefined }}
                        >
                          <Icon className="w-5 h-5" style={{ color: branding.primary_color }} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Brand Color</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {COLORS.map(({ name, value }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateBranding("primary_color", value)}
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            branding.primary_color === value ? "ring-2 ring-offset-2 scale-110" : ""
                          }`}
                          style={{ backgroundColor: value, borderColor: branding.primary_color === value ? "#1a1a2e" : "transparent" }}
                          title={name}
                        />
                      ))}
                      <input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => updateBranding("primary_color", e.target.value)}
                        className="w-10 h-10 rounded-full cursor-pointer border-2 border-dashed border-gray-300"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-gray-500">Show this portal to users</p>
                    </div>
                    <Switch checked={basics.is_active} onCheckedChange={(v) => updateBasics("is_active", v)} />
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* BRANDING & FLOW TAB */}
            <TabsContent value="branding" className="p-6 space-y-6 m-0">
              {/* Phase Toggle Cards */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Layers className="w-5 h-5" /> Invite Flow Phases
                </h3>
                <div className="grid grid-cols-5 gap-3">
                  {PHASES.map((phase) => {
                    const Icon = phase.icon;
                    const isEnabled = phases[phase.id]?.enabled !== false;
                    return (
                      <button
                        key={phase.id}
                        onClick={() => togglePhase(phase.id)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          isEnabled ? "shadow-md" : "border-gray-200 opacity-50"
                        }`}
                        style={{ borderColor: isEnabled ? branding.primary_color : undefined }}
                      >
                        <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: isEnabled ? branding.primary_color : "#9ca3af" }} />
                        <p className="text-sm font-medium">{phase.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{isEnabled ? "Enabled" : "Disabled"}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Envelope Settings */}
                <Card className={phases.envelope?.enabled === false ? "opacity-50" : ""}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> Envelope Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Envelope Text</Label>
                      <Input value={branding.envelope_text} onChange={(e) => updateBranding("envelope_text", e.target.value)} placeholder="YOU'RE INVITED" disabled={phases.envelope?.enabled === false} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Background</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={branding.envelope_bg_color} onChange={(e) => updateBranding("envelope_bg_color", e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                          <Input value={branding.envelope_bg_color} onChange={(e) => updateBranding("envelope_bg_color", e.target.value)} className="font-mono text-xs flex-1" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Text</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={branding.envelope_text_color} onChange={(e) => updateBranding("envelope_text_color", e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                          <Input value={branding.envelope_text_color} onChange={(e) => updateBranding("envelope_text_color", e.target.value)} className="font-mono text-xs flex-1" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Welcome Settings */}
                <Card className={phases.welcome?.enabled === false ? "opacity-50" : ""}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2"><Home className="w-4 h-4" /> Welcome Page</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Welcome Title</Label>
                      <Input value={branding.welcome_title} onChange={(e) => updateBranding("welcome_title", e.target.value)} placeholder="Welcome to your portal!" disabled={phases.welcome?.enabled === false} />
                    </div>
                    <div>
                      <Label className="text-xs">Subtitle</Label>
                      <Textarea value={branding.welcome_subtitle} onChange={(e) => updateBranding("welcome_subtitle", e.target.value)} placeholder="We're excited..." rows={2} disabled={phases.welcome?.enabled === false} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show Demo Content</Label>
                      <Switch checked={branding.welcome_show_demo} onCheckedChange={(v) => updateBranding("welcome_show_demo", v)} disabled={phases.welcome?.enabled === false} />
                    </div>
                  </CardContent>
                </Card>
                
                {/* NDA Settings */}
                <Card className={phases.nda?.enabled === false ? "opacity-50" : ""}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> NDA Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">NDA Title</Label>
                      <Input value={branding.nda_title} onChange={(e) => updateBranding("nda_title", e.target.value)} placeholder="Pilot Partner Agreement" disabled={phases.nda?.enabled === false} />
                    </div>
                    <div>
                      <Label className="text-xs">Checkbox Text</Label>
                      <Input value={branding.nda_checkbox_text} onChange={(e) => updateBranding("nda_checkbox_text", e.target.value)} placeholder="I agree to..." disabled={phases.nda?.enabled === false} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show Stealth Mode Badge</Label>
                      <Switch checked={branding.nda_show_stealth} onCheckedChange={(v) => updateBranding("nda_show_stealth", v)} disabled={phases.nda?.enabled === false} />
                    </div>
                  </CardContent>
                </Card>
                
                {/* Portal Settings */}
                <Card className={phases.portal?.enabled === false ? "opacity-50" : ""}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Portal Dashboard</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Welcome Message</Label>
                      <Input value={branding.portal_welcome} onChange={(e) => updateBranding("portal_welcome", e.target.value)} placeholder="Welcome!" disabled={phases.portal?.enabled === false} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show Getting Started Checklist</Label>
                      <Switch checked={branding.portal_show_checklist} onCheckedChange={(v) => updateBranding("portal_show_checklist", v)} disabled={phases.portal?.enabled === false} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show Updates Feed</Label>
                      <Switch checked={branding.portal_show_updates} onCheckedChange={(v) => updateBranding("portal_show_updates", v)} disabled={phases.portal?.enabled === false} />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Envelope Preview */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Envelope Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-40 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: branding.envelope_bg_color }}>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: branding.primary_color }}>
                      {(() => { const Icon = ICONS[basics.icon] || Sparkles; return <Icon className="w-8 h-8 text-white" />; })()}
                    </div>
                    <p className="text-xl font-bold tracking-widest uppercase" style={{ color: branding.envelope_text_color }}>
                      {branding.envelope_text || "YOU'RE INVITED"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* INTAKE QUESTIONS TAB with Drag-and-Drop */}
            <TabsContent value="intake" className="p-0 m-0">
              <div className="flex h-[600px]">
                {/* Left: Question Editor */}
                <div className="flex-1 border-r overflow-y-auto p-4">
                  {loadingTemplate ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
                      <span className="text-gray-500">Loading template...</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Intake Sections</h3>
                        <Button onClick={addSection} size="sm">
                          <Plus className="w-4 h-4 mr-1" /> Add Section
                        </Button>
                      </div>
                      
                      {sections.length === 0 ? (
                        <Card className="border-dashed">
                          <CardContent className="py-8 text-center">
                            <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                            <p className="text-gray-500 text-sm">No sections yet</p>
                            <Button onClick={addSection} variant="outline" size="sm" className="mt-3">
                              <Plus className="w-4 h-4 mr-1" /> Add Section
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="space-y-3">
                          {sections.map((section, sIdx) => (
                            <Card key={section.id} className={`overflow-hidden ${previewSection === sIdx ? "ring-2" : ""}`} style={{ ringColor: branding.primary_color }}>
                              <CardHeader className="py-2 px-3 bg-gray-50 border-b cursor-pointer" onClick={() => setPreviewSection(sIdx)}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 flex-1">
                                    {/* Drag Handle + Reorder Buttons */}
                                    <div className="flex flex-col gap-0.5">
                                      <button onClick={(e) => { e.stopPropagation(); moveSectionUp(sIdx); }} disabled={sIdx === 0} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30">
                                        <ArrowUp className="w-3 h-3 text-gray-400" />
                                      </button>
                                      <button onClick={(e) => { e.stopPropagation(); moveSectionDown(sIdx); }} disabled={sIdx >= sections.length - 1} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30">
                                        <ArrowDown className="w-3 h-3 text-gray-400" />
                                      </button>
                                    </div>
                                    <Input
                                      value={section.title}
                                      onChange={(e) => updateSection(sIdx, "title", e.target.value)}
                                      className="h-7 font-semibold text-sm max-w-[150px]"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Input
                                      value={section.subtitle || ""}
                                      onChange={(e) => updateSection(sIdx, "subtitle", e.target.value)}
                                      className="h-7 text-xs text-gray-500 max-w-[180px]"
                                      placeholder="Subtitle"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Badge variant="outline" className="text-xs">{section.questions.length}</Badge>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteSection(sIdx); }} className="text-red-500 hover:text-red-700 h-7 w-7 p-0">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="p-2 space-y-2">
                                {section.questions.map((q, qIdx) => (
                                  <div key={q.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-sm">
                                    {/* Question Reorder Buttons */}
                                    <div className="flex flex-col gap-0.5 pt-1">
                                      <button onClick={() => moveQuestionUp(sIdx, qIdx)} disabled={qIdx === 0} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30">
                                        <ArrowUp className="w-3 h-3 text-gray-400" />
                                      </button>
                                      <button onClick={() => moveQuestionDown(sIdx, qIdx)} disabled={qIdx >= section.questions.length - 1} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30">
                                        <ArrowDown className="w-3 h-3 text-gray-400" />
                                      </button>
                                    </div>
                                    <div className="flex-1 grid grid-cols-4 gap-1.5">
                                      <Input
                                        value={q.label}
                                        onChange={(e) => updateQuestion(sIdx, qIdx, "label", e.target.value)}
                                        placeholder="Label"
                                        className="col-span-2 h-7 text-xs"
                                      />
                                      <select
                                        value={q.type}
                                        onChange={(e) => updateQuestion(sIdx, qIdx, "type", e.target.value)}
                                        className="h-7 rounded border border-gray-200 text-xs px-1"
                                      >
                                        {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                      </select>
                                      <div className="flex items-center gap-1">
                                        <label className="flex items-center gap-0.5 text-xs">
                                          <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(sIdx, qIdx, "required", e.target.checked)} className="rounded w-3 h-3" />
                                          Req
                                        </label>
                                        <label className="flex items-center gap-0.5 text-xs">
                                          <input type="checkbox" checked={q.has_other} onChange={(e) => updateQuestion(sIdx, qIdx, "has_other", e.target.checked)} className="rounded w-3 h-3" />
                                          Other
                                        </label>
                                      </div>
                                      {(q.type === "multiselect" || q.type === "select") && (
                                        <Textarea
                                          value={(q.options || []).join("\n")}
                                          onChange={(e) => updateQuestion(sIdx, qIdx, "options", e.target.value.split("\n").map(o => o.trim()).filter(Boolean))}
                                          placeholder="Options (one per line)"
                                          className="col-span-3 text-xs min-h-[60px]"
                                          rows={3}
                                        />
                                      )}
                                      <Input
                                        value={q.placeholder || ""}
                                        onChange={(e) => updateQuestion(sIdx, qIdx, "placeholder", e.target.value)}
                                        placeholder="Placeholder"
                                        className="col-span-3 h-7 text-xs"
                                      />
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => deleteQuestion(sIdx, qIdx)} className="text-red-400 hover:text-red-600 h-6 w-6 p-0">
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                                <Button variant="outline" size="sm" onClick={() => addQuestion(sIdx)} className="w-full border-dashed h-7 text-xs">
                                  <Plus className="w-3 h-3 mr-1" /> Add Question
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Right: Real-time Preview */}
                <div className="w-[380px] bg-gray-100 p-4 overflow-y-auto">
                  <div className="sticky top-0 bg-gray-100 pb-3 mb-3 border-b">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Eye className="w-4 h-4" /> Real-time Preview
                    </h3>
                    <p className="text-xs text-gray-500">How users will see this section</p>
                  </div>
                  
                  {currentSection ? (
                    <div className="bg-white rounded-xl shadow-sm p-4">
                      <div className="mb-4 pb-3 border-b">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.primary_color }}>
                            <Settings className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm">{currentSection.title || "Section Title"}</h4>
                            {currentSection.subtitle && <p className="text-xs text-gray-500">{currentSection.subtitle}</p>}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {currentSection.questions.length === 0 ? (
                          <p className="text-center text-gray-400 text-sm py-8">Add questions to see preview</p>
                        ) : (
                          currentSection.questions.map((q) => (
                            <IntakeQuestionPreview key={q.id} question={q} color={branding.primary_color} />
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500 text-sm">Select a section to preview</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            
            {/* PREVIEW TAB */}
            <TabsContent value="preview" className="p-6 m-0">
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> Envelope Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: branding.envelope_bg_color }}>
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: branding.primary_color }}>
                        {(() => { const Icon = ICONS[basics.icon] || Sparkles; return <Icon className="w-8 h-8 text-white" />; })()}
                      </div>
                      <p className="text-xl font-bold tracking-widest" style={{ color: branding.envelope_text_color }}>{branding.envelope_text}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Intake Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sections.map((s) => (
                        <div key={s.id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="font-medium text-sm">{s.title}</span>
                          <Badge variant="outline">{s.questions.length} questions</Badge>
                        </div>
                      ))}
                      {sections.length === 0 && <p className="text-gray-400 text-center py-4">No intake sections</p>}
                    </div>
                    <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                      Total: <strong>{sections.reduce((acc, s) => acc + s.questions.length, 0)}</strong> questions in <strong>{sections.length}</strong> sections
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="col-span-2">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Invite Flow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      {PHASES.map((phase, i) => {
                        const Icon = phase.icon;
                        const isEnabled = phases[phase.id]?.enabled !== false;
                        return (
                          <div key={phase.id} className="flex items-center">
                            <div className={`p-3 rounded-xl text-center ${isEnabled ? "" : "opacity-30"}`}>
                              <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: isEnabled ? branding.primary_color : "#e5e7eb" }}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <p className="text-xs font-medium">{phase.label}</p>
                            </div>
                            {i < PHASES.length - 1 && (
                              <ChevronRight className={`w-5 h-5 mx-2 ${isEnabled && phases[PHASES[i+1].id]?.enabled !== false ? "text-gray-400" : "text-gray-200"}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
        
        <DialogFooter className="px-6 py-4 border-t bg-gray-50 shrink-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-portal-btn">
            {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{existingPortal ? "Save Changes" : "Create Portal"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
